import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { SnugAppQueryProvider } from 'components/OnboardingQueryProvider'
import {
  useOfferAttributes,
  useUpdateOfferAttributes,
} from 'hooks/useOfferAttributesQueries'
import moment from 'moment'
import type { LeaseDisclosures } from 'services/offerAttributesApi'
import styled from 'styled-components'

import { Alert, Box, Flex } from 'app/components/design-system-components'
import CollapsableSection from 'app/components/design-system-components/CollapsableSection'
import { Spinner } from 'app/components/design-system-components/Spinner'
import * as Text from 'app/components/display/text/text_component'
import { CheckBoxGeneral } from 'app/components/forms/forms'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import {
  isLeaseDisclosuresComplete,
  isPropertyLive,
} from 'app/pages/teams/onlist/components/Disclosures/leaseDisclosures'
import { isSouthAustralia } from 'app/utils/address/helpers'

// Styled components following existing patterns
const SectionSubtext = styled.div`
  font-size: ${theme.fontSizes.pLarge16};
  color: #666;
  margin-bottom: 20px;
`

const AuditLogText = styled.div`
  font-size: ${theme.fontSizes.pRegular14};
  color: #333;
  margin-bottom: 8px;
`

const FieldLabel = styled.div`
  font-weight: ${theme.fontWeights.bold};
  font-size: ${theme.fontSizes.pLarge16};
  margin-bottom: 8px;
`

const StyledTextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: ${theme.fontSizes.pLarge16};
  font-family: inherit;
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`

const SavingIndicator = styled.span`
  color: #666;
  font-size: ${theme.fontSizes.pRegular14};
`

const StyledInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: ${theme.fontSizes.pLarge16};
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`

interface SAFormA1FieldsSectionProps {
  teamGuid: string
  offerGuid: string
  isEditable?: boolean
  propertyState?: string
  propertyStatus?: number | null
  requireLeasingDisclosuresBeforePublishing?: boolean
}

// Helper to check if any lease disclosure values have been set
const hasAnyLeaseDisclosureValues = (
  leaseDisclosures: LeaseDisclosures | undefined,
): boolean => {
  if (!leaseDisclosures) return false

  return !!(
    leaseDisclosures.exclusions ||
    leaseDisclosures.landlord_intends_to_sell !== undefined ||
    leaseDisclosures.water_responsibility ||
    leaseDisclosures.water_details ||
    leaseDisclosures.electricity_responsibility ||
    leaseDisclosures.gas_responsibility ||
    leaseDisclosures.other_charges
  )
}

export const SAFormA1FieldsSectionInner: React.FC<SAFormA1FieldsSectionProps> =
  ({
    teamGuid,
    offerGuid,
    isEditable = true,
    propertyState,
    propertyStatus,
    requireLeasingDisclosuresBeforePublishing = false,
  }) => {
    const { data: offerAttributes, isLoading } = useOfferAttributes(
      teamGuid,
      offerGuid,
    )
    const updateMutation = useUpdateOfferAttributes()

    const leaseDisclosures = offerAttributes?.lease_disclosures
    const disclosuresComplete = useMemo(
      () => isLeaseDisclosuresComplete(leaseDisclosures ?? {}),
      [leaseDisclosures],
    )

    const [exclusions, setExclusions] = useState('')
    const [landlordIntendsToSell, setLandlordIntendsToSell] =
      useState<boolean | null>(null)
    const [waterResponsibility, setWaterResponsibility] =
      useState<'tenant' | 'landlord' | null>(null)
    const [waterDetails, setWaterDetails] = useState('')
    const [electricityResponsibility, setElectricityResponsibility] =
      useState<'tenant' | 'landlord' | null>(null)
    const [gasResponsibility, setGasResponsibility] =
      useState<'tenant' | 'landlord' | 'not_applicable' | null>(null)
    const [otherCharges, setOtherCharges] = useState('')

    // Sync ALL local state with fetched data in a single useEffect
    // This ensures all fields are populated correctly when the component remounts
    useEffect(() => {
      if (leaseDisclosures) {
        setExclusions(leaseDisclosures.exclusions || '')
        setLandlordIntendsToSell(
          leaseDisclosures.landlord_intends_to_sell ?? null,
        )
        setWaterResponsibility(leaseDisclosures.water_responsibility ?? null)
        setWaterDetails(leaseDisclosures.water_details || '')
        setElectricityResponsibility(
          leaseDisclosures.electricity_responsibility ?? null,
        )
        setGasResponsibility(leaseDisclosures.gas_responsibility ?? null)
        setOtherCharges(leaseDisclosures.other_charges || '')
      }
    }, [leaseDisclosures])

    const latestPublishAudit = useMemo(() => {
      const publishedEntries =
        offerAttributes?.lease_disclosures_audit?.published
      if (!publishedEntries || publishedEntries.length === 0) {
        return null
      }
      const [firstEntry, ...rest] = publishedEntries
      if (!firstEntry) {
        return null
      }
      const firstEntryTime = Date.parse(firstEntry.published_at)
      const initialLatest = {
        audit: firstEntry,
        time: Number.isNaN(firstEntryTime) ? -Infinity : firstEntryTime,
      }
      const latestWithTime = rest.reduce((latest, entry) => {
        const entryTime = Date.parse(entry.published_at)
        if (Number.isNaN(entryTime)) {
          return latest
        }
        if (entryTime > latest.time) {
          return { audit: entry, time: entryTime }
        }
        return latest
      }, initialLatest)
      return latestWithTime.audit
    }, [offerAttributes?.lease_disclosures_audit?.published])

    const publishAuditText = useMemo(() => {
      if (!latestPublishAudit) return ''
      const publishedAt = moment(latestPublishAudit.published_at)
      if (!publishedAt.isValid()) return ''
      return `Published live by ${
        latestPublishAudit.published_by_name
      } ${publishedAt.format('h:mm:ssa ddd D MMM YYYY')}`
    }, [latestPublishAudit])

    // Helper function to build complete lease_disclosures object for full replacement semantics.
    // Only includes properties that have values (exactOptionalPropertyTypes compliance).
    const buildLeaseDisclosures = useCallback((): LeaseDisclosures => {
      const result: LeaseDisclosures = {}

      if (exclusions) result.exclusions = exclusions
      if (landlordIntendsToSell !== null)
        result.landlord_intends_to_sell = landlordIntendsToSell
      if (waterResponsibility) result.water_responsibility = waterResponsibility
      if (waterDetails) result.water_details = waterDetails
      if (electricityResponsibility)
        result.electricity_responsibility = electricityResponsibility
      if (gasResponsibility) result.gas_responsibility = gasResponsibility
      if (otherCharges) result.other_charges = otherCharges

      return result
    }, [
      exclusions,
      landlordIntendsToSell,
      waterResponsibility,
      waterDetails,
      electricityResponsibility,
      gasResponsibility,
      otherCharges,
    ])

    const handleExclusionsBlur = useCallback(() => {
      // Only update if value changed
      if (exclusions !== (leaseDisclosures?.exclusions || '')) {
        updateMutation.mutate({
          teamGuid,
          offerGuid,
          leaseDisclosures: buildLeaseDisclosures(),
        })
      }
    }, [
      teamGuid,
      offerGuid,
      exclusions,
      leaseDisclosures?.exclusions,
      updateMutation.mutate,
      buildLeaseDisclosures,
    ])

    const handleYesClick = useCallback(() => {
      const newValue = landlordIntendsToSell === true ? null : true
      setLandlordIntendsToSell(newValue)
      // Need to build the complete object with the new value
      // Since buildLeaseDisclosures uses current state, we need to pass the new value
      updateMutation.mutate({
        teamGuid,
        offerGuid,
        leaseDisclosures: {
          ...buildLeaseDisclosures(),
          landlord_intends_to_sell: newValue,
        },
      })
    }, [
      teamGuid,
      offerGuid,
      landlordIntendsToSell,
      updateMutation.mutate,
      buildLeaseDisclosures,
    ])

    const handleNoClick = useCallback(() => {
      const newValue = landlordIntendsToSell === false ? null : false
      setLandlordIntendsToSell(newValue)
      // Need to build the complete object with the new value
      updateMutation.mutate({
        teamGuid,
        offerGuid,
        leaseDisclosures: {
          ...buildLeaseDisclosures(),
          landlord_intends_to_sell: newValue,
        },
      })
    }, [
      teamGuid,
      offerGuid,
      landlordIntendsToSell,
      updateMutation.mutate,
      buildLeaseDisclosures,
    ])

    // Water responsibility handlers
    const handleWaterTenantClick = useCallback(() => {
      const newValue = waterResponsibility === 'tenant' ? null : 'tenant'
      setWaterResponsibility(newValue)
      updateMutation.mutate({
        teamGuid,
        offerGuid,
        leaseDisclosures: {
          ...buildLeaseDisclosures(),
          water_responsibility: newValue,
        },
      })
    }, [
      teamGuid,
      offerGuid,
      waterResponsibility,
      updateMutation.mutate,
      buildLeaseDisclosures,
    ])

    const handleWaterLandlordClick = useCallback(() => {
      const newValue = waterResponsibility === 'landlord' ? null : 'landlord'
      setWaterResponsibility(newValue)
      updateMutation.mutate({
        teamGuid,
        offerGuid,
        leaseDisclosures: {
          ...buildLeaseDisclosures(),
          water_responsibility: newValue,
        },
      })
    }, [
      teamGuid,
      offerGuid,
      waterResponsibility,
      updateMutation.mutate,
      buildLeaseDisclosures,
    ])

    const handleWaterDetailsBlur = useCallback(() => {
      if (waterDetails !== (leaseDisclosures?.water_details || '')) {
        updateMutation.mutate({
          teamGuid,
          offerGuid,
          leaseDisclosures: buildLeaseDisclosures(),
        })
      }
    }, [
      teamGuid,
      offerGuid,
      waterDetails,
      leaseDisclosures?.water_details,
      updateMutation.mutate,
      buildLeaseDisclosures,
    ])

    // Electricity responsibility handlers
    const handleElectricityTenantClick = useCallback(() => {
      const newValue = electricityResponsibility === 'tenant' ? null : 'tenant'
      setElectricityResponsibility(newValue)
      updateMutation.mutate({
        teamGuid,
        offerGuid,
        leaseDisclosures: {
          ...buildLeaseDisclosures(),
          electricity_responsibility: newValue,
        },
      })
    }, [
      teamGuid,
      offerGuid,
      electricityResponsibility,
      updateMutation.mutate,
      buildLeaseDisclosures,
    ])

    const handleElectricityLandlordClick = useCallback(() => {
      const newValue =
        electricityResponsibility === 'landlord' ? null : 'landlord'
      setElectricityResponsibility(newValue)
      updateMutation.mutate({
        teamGuid,
        offerGuid,
        leaseDisclosures: {
          ...buildLeaseDisclosures(),
          electricity_responsibility: newValue,
        },
      })
    }, [
      teamGuid,
      offerGuid,
      electricityResponsibility,
      updateMutation.mutate,
      buildLeaseDisclosures,
    ])

    // Gas responsibility handlers
    const handleGasTenantClick = useCallback(() => {
      const newValue = gasResponsibility === 'tenant' ? null : 'tenant'
      setGasResponsibility(newValue)
      updateMutation.mutate({
        teamGuid,
        offerGuid,
        leaseDisclosures: {
          ...buildLeaseDisclosures(),
          gas_responsibility: newValue,
        },
      })
    }, [
      teamGuid,
      offerGuid,
      gasResponsibility,
      updateMutation.mutate,
      buildLeaseDisclosures,
    ])

    const handleGasLandlordClick = useCallback(() => {
      const newValue = gasResponsibility === 'landlord' ? null : 'landlord'
      setGasResponsibility(newValue)
      updateMutation.mutate({
        teamGuid,
        offerGuid,
        leaseDisclosures: {
          ...buildLeaseDisclosures(),
          gas_responsibility: newValue,
        },
      })
    }, [
      teamGuid,
      offerGuid,
      gasResponsibility,
      updateMutation.mutate,
      buildLeaseDisclosures,
    ])

    const handleGasNotApplicableClick = useCallback(() => {
      const newValue =
        gasResponsibility === 'not_applicable' ? null : 'not_applicable'
      setGasResponsibility(newValue)
      updateMutation.mutate({
        teamGuid,
        offerGuid,
        leaseDisclosures: {
          ...buildLeaseDisclosures(),
          gas_responsibility: newValue,
        },
      })
    }, [
      teamGuid,
      offerGuid,
      gasResponsibility,
      updateMutation.mutate,
      buildLeaseDisclosures,
    ])

    // Other charges handler
    const handleOtherChargesBlur = useCallback(() => {
      if (otherCharges !== (leaseDisclosures?.other_charges || '')) {
        updateMutation.mutate({
          teamGuid,
          offerGuid,
          leaseDisclosures: buildLeaseDisclosures(),
        })
      }
    }, [
      teamGuid,
      offerGuid,
      otherCharges,
      leaseDisclosures?.other_charges,
      updateMutation.mutate,
      buildLeaseDisclosures,
    ])

    // Show loading spinner during initial data fetch
    if (isLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Spinner fontSize="32px" />
        </Box>
      )
    }

    // Determine initial collapsed state:
    // 1. If any lease disclosure values exist → always expanded (regardless of state)
    // 2. If no values and NOT in SA → collapsed
    // 3. If no values and in SA → expanded
    const hasValues = hasAnyLeaseDisclosureValues(leaseDisclosures)
    const initiallyCollapsed = hasValues
      ? false
      : !isSouthAustralia(propertyState ?? '')

    const titleSection = (
      <Box>
        {isPropertyLive(propertyStatus) && publishAuditText && (
          <AuditLogText>{publishAuditText}</AuditLogText>
        )}
        <Text.Stronger
          text="Lease Disclosures"
          textClass="fs24"
          componentClass="mb0"
          imgSrc={undefined}
        />
        <SectionSubtext style={{ marginTop: '4px', marginBottom: '0' }}>
          Required for South Australian Form A1 applications.
        </SectionSubtext>
      </Box>
    )

    return (
      <Box mb="40px">
        <CollapsableSection
          TitleSection={titleSection}
          initiallyCollapsed={initiallyCollapsed}
          variant="transparent"
        >
          <Box pt="16px">
            {requireLeasingDisclosuresBeforePublishing && (
              <Alert
                variant={
                  disclosuresComplete ? 'successWithBg' : 'warningWithBg'
                }
                mb="16px"
              >
                {disclosuresComplete
                  ? 'Lease disclosures complete. You can publish this property.'
                  : 'You must complete lease disclosures before you can publish this property.'}
              </Alert>
            )}
            {/* Landlord intends to sell - YES/NO checkboxes */}
            <Box mb="20px">
              <FieldLabel>
                The landlord has advertised, intends to advertise or has entered
                into an existing sales agency agreement for the sale of the
                premises
              </FieldLabel>
              <Box display="flex" gap="20px" mt="8px">
                <CheckBoxGeneral
                  label="Yes"
                  checked={landlordIntendsToSell === true}
                  disabled={!isEditable || isLoading}
                  eventProcessedByComponent
                  id="landlord-intends-to-sell-yes"
                  onChange={handleYesClick}
                />
                <CheckBoxGeneral
                  label="No"
                  checked={landlordIntendsToSell === false}
                  disabled={!isEditable || isLoading}
                  eventProcessedByComponent
                  id="landlord-intends-to-sell-no"
                  onChange={handleNoClick}
                />
              </Box>
            </Box>

            {/* Exclusions - EDITABLE */}
            <Box mb="20px">
              <FieldLabel>Exclusions from property lease</FieldLabel>
              <StyledTextArea
                value={exclusions}
                onChange={(e) => setExclusions(e.target.value)}
                onBlur={handleExclusionsBlur}
                disabled={!isEditable || isLoading}
                placeholder="Enter any exclusions from the property lease..."
                rows={3}
              />
            </Box>

            {/* Leasing Rates & Charges Section */}
            <Box>
              <FieldLabel>
                Rates and charges for the following prescribed services will be
                paid by (tick one box in each case)
              </FieldLabel>

              {/* Water */}
              <Box mb="20px" mt="12px">
                <FieldLabel>Water</FieldLabel>
                <Box display="flex" gap="20px" mt="8px">
                  <CheckBoxGeneral
                    label="Tenant"
                    checked={waterResponsibility === 'tenant'}
                    disabled={!isEditable || isLoading}
                    eventProcessedByComponent
                    id="water-tenant"
                    onChange={handleWaterTenantClick}
                  />
                  <CheckBoxGeneral
                    label="Landlord"
                    checked={waterResponsibility === 'landlord'}
                    disabled={!isEditable || isLoading}
                    eventProcessedByComponent
                    id="water-landlord"
                    onChange={handleWaterLandlordClick}
                  />
                </Box>
                <Box mt="8px">
                  <StyledInput
                    type="text"
                    value={waterDetails}
                    onChange={(e) => setWaterDetails(e.target.value)}
                    onBlur={handleWaterDetailsBlur}
                    disabled={!isEditable || isLoading}
                    placeholder="Details"
                  />
                </Box>
              </Box>

              {/* Electricity */}
              <Box mb="20px">
                <FieldLabel>Electricity</FieldLabel>
                <Box display="flex" gap="20px" mt="8px">
                  <CheckBoxGeneral
                    label="Tenant"
                    checked={electricityResponsibility === 'tenant'}
                    disabled={!isEditable || isLoading}
                    eventProcessedByComponent
                    id="electricity-tenant"
                    onChange={handleElectricityTenantClick}
                  />
                  <CheckBoxGeneral
                    label="Landlord"
                    checked={electricityResponsibility === 'landlord'}
                    disabled={!isEditable || isLoading}
                    eventProcessedByComponent
                    id="electricity-landlord"
                    onChange={handleElectricityLandlordClick}
                  />
                </Box>
              </Box>

              {/* Gas */}
              <Box mb="20px">
                <FieldLabel>Gas</FieldLabel>
                <Box display="flex" gap="20px" mt="8px">
                  <CheckBoxGeneral
                    label="Tenant"
                    checked={gasResponsibility === 'tenant'}
                    disabled={!isEditable || isLoading}
                    eventProcessedByComponent
                    id="gas-tenant"
                    onChange={handleGasTenantClick}
                  />
                  <CheckBoxGeneral
                    label="Landlord"
                    checked={gasResponsibility === 'landlord'}
                    disabled={!isEditable || isLoading}
                    eventProcessedByComponent
                    id="gas-landlord"
                    onChange={handleGasLandlordClick}
                  />
                  <CheckBoxGeneral
                    label="Not applicable"
                    checked={gasResponsibility === 'not_applicable'}
                    disabled={!isEditable || isLoading}
                    eventProcessedByComponent
                    id="gas-not-applicable"
                    onChange={handleGasNotApplicableClick}
                  />
                </Box>
              </Box>

              {/* Other charges */}
              <Box>
                <FieldLabel>
                  Specify any other charges or effects on charges
                </FieldLabel>
                <StyledTextArea
                  value={otherCharges}
                  onChange={(e) => setOtherCharges(e.target.value)}
                  onBlur={handleOtherChargesBlur}
                  disabled={!isEditable || isLoading}
                  placeholder="Enter any other charges or effects on charges..."
                  rows={3}
                />
              </Box>
            </Box>

            {/* Section-level saving indicator - placed at end to avoid field shifting */}
            {updateMutation.isPending && (
              <Flex alignItems="center" gap="8px" mt="16px">
                <Spinner fontSize="14px" />
                <SavingIndicator>Saving...</SavingIndicator>
              </Flex>
            )}
          </Box>
        </CollapsableSection>
      </Box>
    )
  }

/**
 * Wrapper component that provides QueryClientProvider context.
 * Use this when rendering SAFormA1FieldsSection outside of existing QueryClientProvider.
 */
export const SAFormA1FieldsSection: React.FC<SAFormA1FieldsSectionProps> = (
  props,
) => {
  return (
    <SnugAppQueryProvider>
      <SAFormA1FieldsSectionInner {...props} />
    </SnugAppQueryProvider>
  )
}

export default SAFormA1FieldsSection
